import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUturnDownIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';

const DepotReturns = () => {
  const [products, setProducts] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    notes: ''
  });
  const [items, setItems] = useState([
    { product: '', quantity: '', unitPrice: '', batchNumber: '', expiryDate: '' }
  ]);

  useEffect(() => {
    fetchData();
  }, [page, limit, filters]);

  const fetchData = async () => {
    try {
      const [productsRes, returnsRes] = await Promise.all([
        axios.get('/api/products', { params: { limit: 1000 } }),
        axios.get('/api/transactions', {
          params: {
            type: 'agent_return',
            page,
            limit,
            search: filters.search || undefined,
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined
          }
        })
      ]);
      setProducts(productsRes.data.products || []);
      setReturns(returnsRes.data.transactions || []);
      setTotalPages(returnsRes.data.totalPages || 1);
      setTotal(returnsRes.data.total || 0);
    } catch (fetchError) {
      console.error('Error fetching depot returns data:', fetchError);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { product: '', quantity: '', unitPrice: '', batchNumber: '', expiryDate: '' }
    ]);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post('/api/transactions/agent-return', {
        items: items.map((item) => ({
          product: item.product,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          batchNumber: item.batchNumber,
          expiryDate: item.expiryDate
        })),
        notes: formData.notes
      });

      setFormData({
        notes: ''
      });
      setItems([{ product: '', quantity: '', unitPrice: '', batchNumber: '', expiryDate: '' }]);

      fetchData();
    } catch (submitError) {
      console.error('Error recording depot return:', submitError);
      setError(
        submitError.response?.data?.message || 'Failed to record return. Try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Depot Returns</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Record unsold items returned to the depot.
          </p>
        </div>
        <div className="flex items-center gap-2 text-primary-600">
          <ArrowUturnDownIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Return Entry</span>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Record Return</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-secondary-700">Items</label>
                <button type="button" className="text-primary-600 text-sm" onClick={addItem}>
                  Add item
                </button>
              </div>
              {items.map((item, index) => (
                <React.Fragment key={index}>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Product</label>
                    <select
                      value={item.product}
                      onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                      className="input-field"
                      required
                    >
                      <option value="">Select product</option>
                      {products.map((product) => (
                        <option key={product._id} value={product._id}>
                          {product.name} ({product.size})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Quantity</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="input-field"
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Unit Price</label>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                      className="input-field"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Batch Number</label>
                    <input
                      type="text"
                      value={item.batchNumber}
                      onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Expiry Date</label>
                    <input
                      type="date"
                      value={item.expiryDate}
                      onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                      className="input-field"
                      required
                    />
                  </div>

                  <div className="flex items-end">
                    {items.length > 1 && (
                      <button
                        type="button"
                        className="text-red-500 text-sm"
                        onClick={() => removeItem(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </React.Fragment>
              ))}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field"
                  rows="3"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Return'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search returns..."
              className="input-field md:col-span-2"
            />
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Recent Returns</h3>
        </div>
        <div className="card-body">
          {returns.length === 0 ? (
            <p className="text-sm text-secondary-500">No depot returns recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Batch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {returns.map((entry) => (
                    <tr key={entry._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.product?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.batchNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
            <div>
              Showing page {page} of {totalPages} · {total} returns
            </div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="input-field h-9"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepotReturns;
